/**
 * Enveloppe Elasticsearch des pages → la forme que la liste /dashboards consomme.
 *
 * La liste est servie par ES (`/api/elasticsearch/dashboards/config/dashboards_list`,
 * `render_mode: decoupled`) : filtrée nativement par les grants (processor
 * content_access), hits plats et constants portant les `delete_url` /
 * `submission_url` du rendu decoupled. Le processor NORMALISE chaque hit vers
 * `{ data:{id,name,slug,ownerUid,tags}, canEdit, submission_url, delete_url,
 * page_url, share_url, transfer_url }` — la forme exacte que lisent déjà les
 * lignes — et CONSTRUIT les URLs par item (adresse front, endpoints
 * partage/transfert d'orbit_dashboard) que reactive-json ne sait pas composer
 * dans le `url` d'un submitData/fetchData.
 *
 * L'ENVELOPPE est conservée (`{contents, aggregations, pagination, …}`) : la page
 * lit `~~.esResult.contents` pour les lignes, `~~.esResult.pagination` pour la
 * pagination serveur et `aggregations.by_tags` pour la facette. Elle est donc
 * intacte aussi bien sur le GET initial que sur les POST de raffinement
 * (recherche, facette, page) — même chemin de code, même forme.
 *
 * `share_url`/`transfer_url` sont toujours construites ; le bouton « Partager »
 * s'affiche à qui a le droit d'ÉDITER (`canEdit`, déduit du `submission_url`
 * par-utilisateur qu'émet le rendu decoupled) — gérer l'accès fait partie de
 * l'édition. Le transfert de propriété reste réservé au propriétaire/admin, gaté
 * dans le panneau par le `canTransfer` de l'endpoint. `ownerUid` est normalisé en
 * Number pour l'affichage, `tags` en tableau de chaînes (les TID reviennent en
 * chaînes du rendu decoupled, en entiers d'ES : une seule forme évite qu'un
 * badge ou une comparaison rate).
 */
const EMPTY_ENVELOPE = { metadata: {}, messages: [], contents: [], aggregations: {}, pagination: { from: 0, size: 25, total: 0 } };

export const dashboardListProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext?.url?.includes('/api/elasticsearch/dashboards/')) return dataToProcess;
  if (responseContext?.status >= 400) return EMPTY_ENVELOPE;
  const contents = dataToProcess?.contents;
  if (!Array.isArray(contents)) return EMPTY_ENVELOPE;

  return {
    ...dataToProcess,
    contents: contents.map((hit) => {
      const ref = encodeURIComponent(hit.slug || String(hit.id));
      return {
        data: {
          id: hit.id,
          name: hit.name,
          slug: hit.slug ?? '',
          ownerUid: hit.ownerUid != null && hit.ownerUid !== '' ? Number(hit.ownerUid) : null,
          tags: Array.isArray(hit.tags) ? hit.tags.map(String) : (hit.tags != null && hit.tags !== '' ? [String(hit.tags)] : []),
        },
        canEdit: !!hit.submission_url,
        // Renommage / retagage en place depuis la liste : l'URL de soumission
        // voyage avec l'item (pattern DataSync) — le YAML n'a rien à construire.
        submission_url: hit.submission_url || null,
        delete_url: hit.delete_url || null,
        // Adresse front de la page : ce qu'un signet enregistre (cf.
        // FavoriteToggle) et ce que la ligne ouvre — construite ici, comme les
        // autres URLs par item.
        page_url: `/dashboards/${ref}`,
        share_url: `/api/orbit/dashboard/${ref}/share`,
        transfer_url: `/api/orbit/dashboard/${ref}/transfer`,
      };
    }),
  };
};
